import React, { useEffect, useRef, useState } from "react";
import { useAppSelector } from "app/store";
import { useGaiaObjectUiContent } from "features/ui-content/hooks";
import {
  Box,
  Button,
  ButtonDropdown,
  ButtonDropdownProps,
  ColumnLayout,
  Container,
  Header,
  Icon,
  Input,
  Select,
  SelectProps,
  SpaceBetween,
  Spinner,
  SplitPanel,
  Textarea,
} from "@amzn/awsui-components-react";
import { FilePreview } from "components/file-preview";
import { isFileTypePreviewable } from "components/file-preview/utils";
import { Document, DocumentVersion } from "models/schemas";
import {
  useGetDocumentQuery,
  useLazySearchDocumentsQuery,
  useSearchDocumentsQuery,
} from "components/documents-table-os/endpoints";
import {
  useDownloadUrlQuery,
  useLazyDownloadUrlQuery,
} from "components/documents-table-os/endpoints";
import { useFileOperationsOS } from "components/documents-table-os/file-operations-os";
import { useFilePreviewSplitPanel } from "components/file-preview-split-panel/hooks";
import { useNotifications } from "features/notifications/hooks";
import { useSelectedTeam } from "features/team-settings/hooks";
import { datePopOver } from "utils/date-format";
import { ValueWithLabel } from "utils/value-with-label";
import { CommonDeleteModal } from "components/common-reuse-components/common-delete-modal";
import MoveFoldersModalOS from "components/move-folders-modal/MoveFoldersModalOS";
import { GaiaConstants } from "utils/gaia-constants";
import { getFileNameWithExtension } from "components/documents-table-os/utils";
import sharedStyles from "components/documents-table-os/style.module.scss";
import styles from "./style.module.scss";
import {
  checkVersionCollisionOS,
  VersionCollisionInfoOS,
} from "components/documents-table-os/version-utils-os";
import VersionConfirmationModalOS from "components/documents-table-os/VersionConfirmationModalOS";

const FilePreviewSplitPanel: React.FC = () => {
  const { addNotification } = useNotifications();
  const { isFileDescriptionEnabled } = useSelectedTeam();
  const { setSplitPanelOpen, setSelectedDocumentId, splitPanel } =
    useFilePreviewSplitPanel();

  // Get props from Redux state
  const documentId = splitPanel.selectedDocumentId;
  const readOnly = splitPanel.source === "documents-readonly";
  const gaiaObjectType = splitPanel.gaiaObjectType;

  // Get context from Redux
  const teamName = useAppSelector((state) => state.audits.teamName);
  const currentUser = useAppSelector((state) => state.auth.user.alias);

  // Determine if this is files tab based on location
  const location = window.location;
  const isFilesTab =
    new URLSearchParams(location.search).get("tabId") === "files" ||
    location.pathname.startsWith("/team-files");
  const uiContent: any = useGaiaObjectUiContent(gaiaObjectType);

  const [document, setDocument] = useState<Document | null>(null);
  const [url, setUrl] = useState<string>("");
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [docToRename, setDocToRename] = useState<Document | undefined>();
  const [fileName, setFileName] = useState("");
  const [selectedDocumentType, setSelectedDocumentType] = useState("");
  const [isEditingType, setIsEditingType] = useState(false);
  const [isTeamFileLevel, setIsTeamFileLevel] = useState(false);
  const [moveModalVisible, setMoveModalVisible] = useState(false);
  const [selectedVersion, setSelectedVersion] =
    useState<DocumentVersion | null>(null);
  const [versionDeleteModalVisible, setVersionDeleteModalVisible] =
    useState(false);
  const [versionToDelete, setVersionToDelete] =
    useState<DocumentVersion | null>(null);
  const [versionRestoreModalVisible, setVersionRestoreModalVisible] =
    useState(false);
  const [versionToRestore, setVersionToRestore] =
    useState<DocumentVersion | null>(null);
  const [versionConfirmModalVisible, setVersionConfirmModalVisible] =
    useState(false);
  const [versionCollisions, setVersionCollisions] = useState<
    VersionCollisionInfoOS[]
  >([]);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState("");

  // Initialize file operations hook
  const parentId = document?.parentId || teamName;
  const fileOps = useFileOperationsOS({
    addNotification,
    teamName,
    currentUser,
    parentId,
    category: "team-files",
  });

  // For collision detection
  const [searchDocuments] = useLazySearchDocumentsQuery();

  // Fetch documents for move modal
  const { data: documentsData } = useSearchDocumentsQuery(
    { teamName, parentId: teamName },
    { skip: !isTeamFileLevel || !teamName },
  );

  // Fetch document data
  const {
    data: docData,
    isLoading: isDocumentLoading,
    isFetching: isDocumentFetching,
  } = useGetDocumentQuery(
    { documentId: documentId! },
    {
      refetchOnMountOrArgChange: true,
      skip: !documentId,
    },
  );

  // Fetch download URL for preview
  // For KB approval docs (readOnly), use s3Key since download-url doesn't support KB-prefix UUIDs
  const downloadUrlId =
    readOnly && document?.s3Key ? document.s3Key : documentId;
  const { data: downloadUrl } = useDownloadUrlQuery(
    {
      documentId: downloadUrlId ?? "",
      preview: true,
      versionId: selectedVersion?.versionId,
    },
    {
      skip: readOnly ? !document?.s3Key : !documentId,
      refetchOnMountOrArgChange: true,
    },
  );

  const [fetchDownloadUrl] = useLazyDownloadUrlQuery();

  const prevDocumentIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (docData?.document) {
      const isNewDocument =
        prevDocumentIdRef.current !== docData.document.documentId;
      if (isNewDocument) {
        prevDocumentIdRef.current = docData.document.documentId;
        setDocument(docData.document);
        setSelectedDocumentType(docData.document.documentType);
        setIsTeamFileLevel(docData.document.documentType === "Team");
      }
    }
  }, [docData]);

  useEffect(() => {
    if (downloadUrl) {
      setUrl(downloadUrl.url);
    }
  }, [downloadUrl]);

  const handleDelete = async () => {
    if (!document) return;

    const success = await fileOps.deleteDocuments([document]);
    if (success) {
      setDeleteModalVisible(false);
      setSplitPanelOpen(false);
      setSelectedDocumentId(null);
    }
  };

  const handleRename = async () => {
    if (!docToRename || !fileName.trim()) return;

    await handleWithVersionCollisionCheck(
      docToRename,
      { fileName: fileName.trim() },
      executeRename,
    );
  };

  const executeRename = async () => {
    if (!docToRename || !fileName.trim()) return;

    const success = await fileOps.renameDocument(docToRename, fileName.trim());
    if (success) {
      setDocument((prev) =>
        prev ? { ...prev, fileName: fileName.trim() } : null,
      );
      setDocToRename(undefined);
      setFileName("");
    }
  };

  const handleDocumentTypeChange = async (newType: string) => {
    if (!document) return;

    await handleWithVersionCollisionCheck(
      document,
      { documentType: newType },
      async () => executeDocumentTypeChange(newType),
    );
  };

  const executeDocumentTypeChange = async (newType: string) => {
    if (!document) return;

    const success = await fileOps.updateDocumentType(document, newType);
    if (success) {
      setDocument((prev) => (prev ? { ...prev, documentType: newType } : null));
      setSelectedDocumentType(newType);
      setIsEditingType(false);
    }
  };

  const handleDescriptionSave = async () => {
    if (!document) return;

    const success = await fileOps.updateDocumentDescription(
      document,
      editedDescription,
    );
    if (success) {
      setDocument((prev) =>
        prev ? { ...prev, description: editedDescription } : null,
      );
      setIsEditingDescription(false);
    }
  };

  /**
   * Helper function to handle operations that may cause version collisions.
   * Checks for collisions and shows confirmation modal if needed, or executes the action directly.
   */
  const handleWithVersionCollisionCheck = async (
    targetDocument: Document,
    changes: Partial<Document>,
    executeAction: () => Promise<void>,
  ) => {
    try {
      const collision = await checkVersionCollisionOS(
        targetDocument,
        changes,
        (params) => searchDocuments(params).unwrap(),
      );

      if (collision.hasCollision) {
        setVersionCollisions([collision]);
        setPendingAction(() => executeAction);
        setVersionConfirmModalVisible(true);
        return;
      }

      await executeAction();
    } catch (error) {
      console.error("Error checking version collision:", error);
      // Proceed with action if collision check fails
      await executeAction();
    }
  };

  const handleDownload = async () => {
    if (!document) return;
    if (readOnly && document.s3Key) {
      try {
        const result = await fetchDownloadUrl({
          documentId: document.s3Key,
          preview: false,
        }).unwrap();
        if (result?.url) {
          window.location.href = result.url;
          return;
        }
      } catch {
        // fallback below
      }
    }
    await fileOps.downloadSingleDoc(document);
  };

  const handleOpenInNewTab = () => {
    if (!url) {
      addNotification({
        type: "error",
        header: "Preview URL not available.",
        dismissible: true,
        timeout: GaiaConstants.NOTIFICATION_TIMEOUT.SHORT,
      });
      return;
    }
    window.open(url, "_blank");
  };

  const handleVersionChange = (detail: SelectProps.ChangeDetail) => {
    const versionId = detail.selectedOption?.value;
    if (!versionId || versionId === "current") {
      setSelectedVersion(null);
    } else {
      const version = document?.versions?.find(
        (v) => v.versionId === versionId,
      );
      if (version) {
        setSelectedVersion(version);
      }
    }
  };

  const handlePathMove = async (files: Document[], targetParentId: string) => {
    // MoveFoldersModalOS now handles collision detection internally
    const success = await fileOps.moveFiles(files, targetParentId);
    if (success) {
      setDocument((prev) =>
        prev ? { ...prev, parentId: targetParentId } : null,
      );
      setMoveModalVisible(false);
      setSplitPanelOpen(false);
      setSelectedDocumentId(null);
    }
  };

  const handleVersionRestoreClick = () => {
    if (selectedVersion) {
      setVersionToRestore(selectedVersion);
      setVersionRestoreModalVisible(true);
    }
  };

  const handleVersionRestore = async () => {
    if (!versionToRestore || !document) return;

    const success = await fileOps.restoreVersion(document, versionToRestore);
    if (success) {
      setVersionRestoreModalVisible(false);
      setVersionToRestore(null);
      setSelectedVersion(null);
    }
  };

  const handleVersionDelete = async () => {
    if (!versionToDelete || !document) return;

    const success = await fileOps.deleteVersion(
      document.documentId,
      versionToDelete.versionId,
    );
    if (success) {
      setSelectedVersion(null);
      setVersionDeleteModalVisible(false);
      setVersionToDelete(null);
    }
  };

  const handleVersionActionClick = (
    event: CustomEvent<ButtonDropdownProps.ItemClickDetails>,
  ) => {
    const actionId = event.detail.id;

    switch (actionId) {
      case "download":
        if (document) {
          fileOps.downloadSingleDoc(document, selectedVersion?.versionId);
        }
        break;
      case "restore":
        handleVersionRestoreClick();
        break;
      case "delete":
        if (selectedVersion) {
          setVersionToDelete(selectedVersion);
          setVersionDeleteModalVisible(true);
        }
        break;
    }
  };

  const handleVersionConfirm = () => {
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
    setVersionCollisions([]);
    setVersionConfirmModalVisible(false);
  };

  const handleActionClick = (
    event: CustomEvent<ButtonDropdownProps.ItemClickDetails>,
  ) => {
    const actionId = event.detail.id;
    const action = actionId.split(".")[0];

    switch (action) {
      case "open":
        handleOpenInNewTab();
        break;
      case "ed":
        if (document) {
          setDocToRename(document);
          setFileName(document.fileName);
        }
        break;
      case "rm":
        setDeleteModalVisible(true);
        break;
    }
  };

  const getHeaderTitle = () => {
    if (!document) return "File Preview";
    if (document.documentId === docToRename?.documentId) {
      return "Renaming file...";
    }
    return getFileNameWithExtension(document);
  };

  const renderFileNameEditor = () => {
    if (
      readOnly ||
      !document ||
      document.documentId !== docToRename?.documentId
    )
      return null;

    return (
      <div className={styles.fileNameEditor}>
        <div className={styles.inputWrapper}>
          <Input
            onChange={({ detail }) => setFileName(detail.value)}
            value={fileName}
          />
        </div>
        <span>{document.extensionType}</span>
        <span
          onClick={() => setDocToRename(undefined)}
          className={sharedStyles.renameButton}
        >
          <Icon name="close" />
        </span>
        <span onClick={handleRename} className={sharedStyles.renameButton}>
          <Icon name="check" />
        </span>
      </div>
    );
  };

  const renderDocumentType = () => {
    if (!document) return null;

    if (isEditingType) {
      const selectOptions =
        uiContent.documentTypeOptions?.map((option: any) => ({
          label: option.label,
          value: option.value,
        })) || [];

      const selectedOption =
        selectOptions.find((opt: any) => opt.value === selectedDocumentType) ||
        null;

      return (
        <div className={styles.flexContainer}>
          <Select
            selectedOption={selectedOption}
            onChange={({ detail }) =>
              handleDocumentTypeChange(detail.selectedOption?.value || "")
            }
            options={selectOptions}
            placeholder="Select document type"
          />
          <Button
            variant="icon"
            iconName="close"
            onClick={() => setIsEditingType(false)}
          />
        </div>
      );
    }

    const currentOption = uiContent.documentTypeOptions?.find(
      (opt: any) => opt.value === selectedDocumentType,
    );

    return (
      <div className={styles.flexContainer}>
        <span>{currentOption?.label || selectedDocumentType}</span>
        {!readOnly && (
          <Button
            variant="icon"
            iconName="edit"
            onClick={() => setIsEditingType(true)}
          />
        )}
      </div>
    );
  };

  const renderPath = () => {
    if (!document || !isTeamFileLevel) return null;

    const currentPath = `/${document.parentId
      .split("/")
      .filter(Boolean)
      .slice(1)
      .join("/")}`;

    return (
      <div className={styles.flexContainer}>
        <span>{currentPath || "/"}</span>
        {!readOnly && (
          <Button
            variant="icon"
            iconName="edit"
            onClick={() => setMoveModalVisible(true)}
            ariaLabel="Edit file location"
          />
        )}
      </div>
    );
  };

  const renderVersionSelector = () => {
    if (!document?.versions || document.versions.length === 0) return null;

    // Create version options matching the old FilePreviewSplitPanel format
    const totalVersions = document.versions.length + 1;
    const versionOptions = [
      {
        label: `Version ${totalVersions} (${datePopOver(
          Number(document.modifiedAt),
        )}) (CURRENT)`,
        value: "current",
      },
      ...document.versions.map((version, index) => ({
        label: `Version ${document.versions!.length - index} (${datePopOver(
          Number(version.modifiedAt),
        )})`,
        value: version.versionId,
      })),
    ];

    const selectedVersionId = selectedVersion?.versionId || "current";
    const selectedOption =
      versionOptions.find((v) => v.value === selectedVersionId) || null;

    return (
      <ValueWithLabel label="Versions">
        <SpaceBetween direction="horizontal" size="xs">
          <div className={styles.versionSelectContainer}>
            <Select
              selectedOption={selectedOption}
              onChange={({ detail }) => handleVersionChange(detail)}
              options={versionOptions}
              placeholder="Select version"
            />
          </div>
          {!readOnly && document.versions.length > 0 && (
            <ButtonDropdown
              items={[
                {
                  id: "download",
                  text: "Download",
                  disabled: false,
                },
                {
                  id: "restore",
                  text: "Restore",
                  disabled: !selectedVersion,
                  disabledReason: "This is the current version",
                },
                {
                  id: "delete",
                  text: "Delete",
                  disabled: !selectedVersion,
                  disabledReason: "This is the current version",
                },
              ]}
              onItemClick={handleVersionActionClick}
            >
              Actions
            </ButtonDropdown>
          )}
        </SpaceBetween>
      </ValueWithLabel>
    );
  };

  if (!documentId) {
    return (
      <SplitPanel
        header="File Preview"
        i18nStrings={{
          closeButtonAriaLabel: "Close panel",
          openButtonAriaLabel: "Open panel",
        }}
        closeBehavior={isFilesTab ? "collapse" : "hide"}
      >
        <Box textAlign="center" padding="l">
          Select a file to preview
        </Box>
      </SplitPanel>
    );
  }

  return (
    <>
      <SplitPanel
        header={getHeaderTitle()}
        i18nStrings={{
          closeButtonAriaLabel: "Close panel",
          openButtonAriaLabel: "Open panel",
        }}
        closeBehavior={isFilesTab ? "collapse" : "hide"}
        hidePreferencesButton
      >
        {renderFileNameEditor()}
        {(isDocumentLoading && !document) || (docData && !document) ? (
          <Container>
            <Box textAlign="center">
              <Spinner /> Loading document...
            </Box>
          </Container>
        ) : document ? (
          <SpaceBetween direction="vertical" size="l">
            <Container
              header={
                <Header
                  variant="h2"
                  actions={
                    readOnly ? (
                      <SpaceBetween direction="horizontal" size="xs">
                        <Button
                          onClick={handleOpenInNewTab}
                          disabled={
                            !isFileTypePreviewable(document.extensionType)
                          }
                        >
                          Open in new tab
                        </Button>
                        <Button onClick={handleDownload} variant="primary">
                          Download
                        </Button>
                      </SpaceBetween>
                    ) : (
                      <SpaceBetween direction="horizontal" size="xs">
                        <ButtonDropdown
                          items={[
                            {
                              id: `open.${document.documentId}`,
                              text: "Open in new tab",
                              disabled: !isFileTypePreviewable(
                                document.extensionType,
                              ),
                              disabledReason:
                                "Preview not available for this file type.",
                            },
                            {
                              id: `ed.${document.documentId}`,
                              text: "Rename",
                            },
                            {
                              id: `rm.${document.documentId}`,
                              text: "Delete",
                            },
                          ]}
                          onItemClick={handleActionClick}
                        >
                          Actions
                        </ButtonDropdown>
                        <Button onClick={handleDownload} variant="primary">
                          Download
                        </Button>
                      </SpaceBetween>
                    )
                  }
                >
                  Details
                </Header>
              }
            >
              <ColumnLayout columns={2} variant="text-grid">
                <SpaceBetween direction="vertical" size="l">
                  {!isTeamFileLevel ? (
                    <ValueWithLabel label="Type">
                      {renderDocumentType()}
                    </ValueWithLabel>
                  ) : (
                    <ValueWithLabel label="Path">{renderPath()}</ValueWithLabel>
                  )}
                  <ValueWithLabel label="File size">
                    {document.size || "-"}
                  </ValueWithLabel>
                </SpaceBetween>
                <SpaceBetween direction="vertical" size="l">
                  <ValueWithLabel label="Last updated">
                    {datePopOver(Number(document.modifiedAt))}
                  </ValueWithLabel>
                  <ValueWithLabel label="Updated by">
                    {document.modifiedBy}
                  </ValueWithLabel>
                </SpaceBetween>
              </ColumnLayout>
              <br />
              {renderVersionSelector()}
              {isFileDescriptionEnabled && (
                <div>
                  <div className={styles.descriptionHeader}>
                    <Box variant="awsui-key-label">Description</Box>
                    {!readOnly && !isEditingDescription && (
                      <Button
                        variant="icon"
                        iconName="edit"
                        onClick={() => {
                          setEditedDescription(document.description || "");
                          setIsEditingDescription(true);
                        }}
                      />
                    )}
                  </div>
                  {isEditingDescription ? (
                    <SpaceBetween direction="vertical" size="xs">
                      <Textarea
                        value={editedDescription}
                        onChange={({ detail }) =>
                          setEditedDescription(detail.value)
                        }
                      />
                      <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                          <Button
                            variant="icon"
                            iconName="close"
                            onClick={() => setIsEditingDescription(false)}
                          />
                          <Button
                            variant="icon"
                            iconName="check"
                            onClick={handleDescriptionSave}
                          />
                        </SpaceBetween>
                      </Box>
                    </SpaceBetween>
                  ) : (
                    <div>{document.description || "-"}</div>
                  )}
                </div>
              )}
            </Container>

            <Container header={<Header variant="h2">Preview</Header>}>
              {url && document.fileName && (
                <FilePreview
                  type={document.extensionType}
                  fileName={document.fileName}
                  presignedUrl={url}
                />
              )}
            </Container>
          </SpaceBetween>
        ) : (
          <Container>
            <Box textAlign="center">Document not found</Box>
          </Container>
        )}
      </SplitPanel>

      <CommonDeleteModal
        setVisible={setDeleteModalVisible}
        visible={deleteModalVisible}
        onDelete={handleDelete}
        items={document}
        title={
          document?.versions && document.versions.length > 0
            ? "Delete all versions?"
            : "Delete file?"
        }
        message={
          document?.versions && document.versions.length > 0 ? (
            <>
              Permanently delete <b>all versions</b> of {document?.fileName}?
            </>
          ) : (
            <>Permanently delete {document?.fileName}?</>
          )
        }
      />

      {isTeamFileLevel && document && (
        <MoveFoldersModalOS
          visible={moveModalVisible}
          setVisible={setMoveModalVisible}
          selectedFiles={[document]}
          teamName={teamName}
          onMove={handlePathMove}
          searchDocuments={(params) => searchDocuments(params).unwrap()}
        />
      )}

      <CommonDeleteModal
        setVisible={setVersionDeleteModalVisible}
        visible={versionDeleteModalVisible}
        onDelete={handleVersionDelete}
        items={versionToDelete}
        title="Delete version?"
        message={<>Permanently delete this version of {document?.fileName}?</>}
      />

      <CommonDeleteModal
        setVisible={setVersionRestoreModalVisible}
        visible={versionRestoreModalVisible}
        onDelete={handleVersionRestore}
        items={versionToRestore}
        title="Restore version?"
        deleteButtonText="Restore"
        message={
          <>
            Restore this version of {document?.fileName}? This will make it the
            current version. All other versions will remain unchanged.
          </>
        }
      />

      <VersionConfirmationModalOS
        visible={versionConfirmModalVisible}
        setVisible={setVersionConfirmModalVisible}
        collisions={versionCollisions}
        onConfirm={handleVersionConfirm}
      />
    </>
  );
};

export default FilePreviewSplitPanel;
